import { useMemo } from 'react';
import { appGeneratePath, slugifyTranslit, type RoutePath } from '@/shared/lib';
import { useTablePresets, type TablePreset } from './useTablePresets';

/**
 * Шаблон адреса раздела закреплённого пресета — единственное место, где он задан.
 * Необязательный сегмент записи входит в шаблон: работа с записью не выводит из раздела,
 * поэтому потребители строят и адрес списка (`id: null`), и адрес записи отсюда же.
 */
export const PINNED_PRESET_ROUTE_PATH = '/preset/:slug/:id?' satisfies RoutePath;

/** Слаг пресета, из названия которого не выходит ни одного адресного символа */
const FALLBACK_SLUG = 'preset';

/** Закреплённый пресет вместе с адресом его раздела */
export type PinnedPresetRoute = {
  /** Сам закреплённый пресет */
  preset: TablePreset;
  /** Слаг пресета — транслитерация названия, различающаяся у закреплённых пресетов */
  slug: string;
  /** Полный адрес раздела пресета */
  path: string;
};

/**
 * Строит адреса разделов закреплённых пресетов.
 *
 * Слаг считается от текущего названия, поэтому переименование меняет и адрес. Совпавший
 * слаг разводится числовым суффиксом, который наращивается до фактически свободного значения:
 * имена пресетов не уникальны, а суффикс сам может совпасть с готовым слагом соседа
 * («Отчёт» второй раз → `otchet-2`, при этом `otchet-2` уже занял пресет «Отчёт 2»).
 */
const buildPinnedPresetRoutes = (presets: TablePreset[]): PinnedPresetRoute[] => {
  const usedSlugs = new Set<string>();
  return presets
    .filter((preset) => preset.pinned)
    .map((preset) => {
      const base = slugifyTranslit(preset.name) || FALLBACK_SLUG;
      let slug = base;
      let suffix = 1;
      while (usedSlugs.has(slug)) {
        suffix += 1;
        slug = `${base}-${suffix}`;
      }
      usedSlugs.add(slug);
      return { preset, slug, path: appGeneratePath(PINNED_PRESET_ROUTE_PATH, { slug, id: null }) };
    });
};

/**
 * Хук адресов закреплённых пресетов таблицы: по пункту меню на каждый и по адресу,
 * который открывает список с применённым пресетом.
 *
 * @param tableName - Имя таблицы (например, `'dialogsListV2'`).
 */
export const usePinnedPresetRoutes = (tableName: string): PinnedPresetRoute[] => {
  const { presets } = useTablePresets(tableName);
  return useMemo(() => buildPinnedPresetRoutes(presets), [presets]);
};
